<template>
  <div class="totals-card">
    <div class="totals-row">
      <span class="totals-label">{{ $t('configurator.subtotal_curtains') }}</span>
      <span class="totals-value">{{ money(subtotal('cortinas')) }}</span>
    </div>
    <div class="totals-row">
      <span class="totals-label">{{ $t('configurator.control_panels') }}</span>
      <span class="totals-value">{{ money(subtotal('cuadro control')) }}</span>
    </div>
    <div class="totals-row">
      <span class="totals-label">{{ $t('configurator.extras') }}</span>
      <span class="totals-value">{{ money(subtotal('extras')) }}</span>
    </div>
    <div class="totals-row totals-row-column">
      <div class="d-flex justify-content-between w-100">
        <span class="totals-label">{{ $t('configurator.packaging') }}</span>
        <span class="totals-value">{{ money(packagingAmount) }}</span>
      </div>
      <div
        v-if="packagingEditable"
        class="d-flex mt-50"
      >
        <button
          v-for="option in packagingOptions"
          :key="option.value"
          type="button"
          class="cuadro-btn cuadro-btn-sm mr-50"
          :class="{ selected: order.packaging_type === option.value }"
          @click="$emit('change-packaging', option.value)"
        >
          {{ option.text }}
        </button>
      </div>
    </div>
    <div class="totals-row">
      <span class="totals-label">{{ $t('configurator.transport') }}</span>
      <span
        v-if="order.shipping_pending || (!order.address.address && packagingEditable)"
        class="cfg-badge cfg-badge-orange"
      >{{ $t('configurator.transport_pending') }}</span>
      <span
        v-else
        class="totals-value"
      >{{ order.shipping_costs ? money(order.shipping_costs) : $t('configurator.pickup_tecnitex') }}</span>
    </div>
    <div
      v-if="showInstallation"
      class="totals-row"
    >
      <span class="totals-label">{{ $t('configurator.installation') }}</span>
      <span class="totals-value">{{ money(order.installation_costs) }}</span>
    </div>
    <div
      v-if="discountAmount"
      class="totals-row"
    >
      <span class="totals-label">{{ $t('configurator.discount') }}</span>
      <span class="totals-value discount">- {{ money(discountAmount) }}</span>
    </div>
    <div class="totals-row">
      <span class="totals-label">{{ $t('configurator.taxable_base') }}</span>
      <span class="totals-value base">{{ money(taxableBase) }}</span>
    </div>
    <div
      v-if="order.amount_iva"
      class="totals-row"
    >
      <span class="totals-label">{{ $t('configurator.vat') }}</span>
      <span class="totals-value">{{ money(order.amount_iva) }}</span>
    </div>
    <div class="totals-row grand-total">
      <span class="totals-label">{{ $t('configurator.total_offer') }}</span>
      <span class="totals-value">{{ money(order.total_amount) }}</span>
    </div>
  </div>
</template>

<script>
import { curtainAmount, money, offerLines } from '../offer'

// Totales de la oferta: resumen del configurador, vista y revisión del pedido
export default {
  props: {
    order: {
      type: Object,
      required: true,
    },
    // El cliente puede elegir estándar / madera (solo mientras configura)
    packagingEditable: {
      type: Boolean,
      default: false,
    },
    // Tecnitex ve siempre la instalación en revisión, aunque sea 0
    alwaysShowInstallation: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    lines() {
      return offerLines(this.order)
    },
    packagingOptions() {
      const options = [{ text: this.$t('configurator.packaging_wood'), value: 'packaging_wood' }]
      if (this.order.packaging_standard) options.unshift({ text: this.$t('configurator.packaging_standard'), value: 'packaging_standard' })
      return options
    },
    packagingAmount() {
      return this.order.packaging_type === 'packaging_wood' ? this.order.packaging_wood : this.order.packaging_standard
    },
    showInstallation() {
      return this.alwaysShowInstallation || Number(this.order.installation_costs) > 0
    },
    discountAmount() {
      const curtains = this.lines.filter(l => l.attr.tipo === 'cortinas')
        .reduce((total, l) => total + (Number(l.line.amount) - curtainAmount(this.order, l.line)) * l.line.quantity, 0)
      // % negativo en la ficha = recargo de tarifa: va dentro del precio de la cortina y no se muestra (como en el PDF)
      return Math.max(curtains, 0) + (Number(this.order.discount_revision) || 0)
    },
    taxableBase() {
      return Number(this.order.total_amount) - (Number(this.order.amount_iva) || 0)
    },
  },
  methods: {
    money,
    subtotal(tipo) {
      return this.lines.filter(l => l.attr.tipo === tipo).reduce((total, l) => {
        const amount = tipo === 'cortinas' ? curtainAmount(this.order, l.line) : Number(l.line.amount)
        return total + amount * l.line.quantity
      }, 0)
    },
  },
}
</script>

<style lang="scss" scoped>
.cuadro-btn-sm {
  padding: .25rem .75rem;
  font-size: .75rem;
}
.totals-card {
  overflow: hidden;
  border: 1.5px solid #e0e0e0;
  border-radius: .428rem;
  background: #fff;
}
.totals-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .75rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: .875rem;
  &:last-child {
    border-bottom: none;
  }
}
.totals-row-column {
  flex-direction: column;
  align-items: flex-start;
}
.totals-label {
  font-weight: 500;
  color: #6e6b7b;
}
.totals-value {
  font-weight: 700;
  color: #1e1e1e;
  &.discount {
    color: #3a7d44;
  }
  &.base {
    font-size: 1rem;
    font-weight: 800;
  }
}
.grand-total {
  padding: 1rem 1.25rem;
  background: #ac1a19;
  .totals-label {
    font-weight: 600;
    color: rgba(255, 255, 255, .85);
  }
  .totals-value {
    font-size: 1.375rem;
    font-weight: 800;
    color: #fff;
  }
}
</style>
